import {
  Check,
  Download,
  ExternalLink,
  FileText,
  MessageSquare,
  Pencil,
  RotateCcw,
} from "lucide-react"

import { PanelCard } from "./PanelCard"
import PanelEmpty from "./PanelEmpty"
import StatusBadge from "../StatusBadge"
import { AvatarStack } from "../Avatar"
import { Button } from "@/components/ui/button"

// Document tab — the manuscript lives in Dibbly Create; this panel is the
// hand-off into the connected editor (read + comment there, approve here). The
// "Open in Dibbly Create" link and the .docx download are the real document
// (order.document.web_link + the final attachment). The download reads only
// deliverables.document, which the server sends once the order may be taken
// delivery of; the writer's unedited draft is never offered. Mirrors
// order-document.html.
export default function DocumentPanel({ order, onOpenModal }) {
  const { title, team = [] } = order
  const collaborators = team.length ? team : [{ initials: "—", name: "Your team" }]

  const documentLink = order.deliverables?.documentLink
  const docFile = order.deliverables?.document
  // The originality & AI report, once the scan has landed (UPDA-86). It sits
  // with the manuscript it scanned, so the Review Scan dialog can point here.
  const report = order.deliverables?.plagiarismReport

  const canApprove = order.permissions?.approveDraft
  const canRevise = order.permissions?.requestRevision
  const sample = order.approvalFlow === "sample"
  // Pre-draft phases have nothing to review yet — unless there's already a real
  // document or file to open.
  const ready =
    !["payment", "scheduled", "production"].includes(order.phase) ||
    canApprove ||
    documentLink ||
    docFile

  if (!ready) {
    return (
      <PanelEmpty
        Icon={FileText}
        title="Document"
        heading="No draft yet"
        body="Your writer is drafting your manuscript. You'll be notified the moment the first draft is ready to review."
      />
    )
  }

  return (
    <PanelCard>
      <div className="flex items-center justify-between border-b border-border px-[22px] py-4 max-[720px]:flex-wrap max-[720px]:gap-2.5 max-[720px]:px-4 max-[720px]:py-[13px]">
        <h3 className="flex items-center gap-[9px] font-display text-[14.5px] font-bold">
          <FileText className="size-[17px] text-primary" strokeWidth={1.9} />
          Document
        </h3>
        {canApprove && <StatusBadge tier="needs_you" label="Draft ready for review" />}
      </div>

      {/* The fixed-width preview column only earns its keep on a real desktop:
          below 1200 it goes full width above the details, the divider moving
          from the right edge to the bottom. */}
      <div className="flex flex-col min-[1200px]:flex-row">
        {/* preview — the manuscript itself lives in Create; this is a branded
            stand-in, not invented manuscript text */}
        <div className="relative flex w-full shrink-0 items-center justify-center border-b border-border bg-accent px-9 py-10 max-[1199px]:px-[22px] max-[1199px]:py-[22px] min-[1200px]:w-[430px] min-[1200px]:border-r min-[1200px]:border-b-0">
          <div className="w-full max-w-[300px] overflow-hidden rounded-md border border-border bg-white px-8 pt-9 pb-10 text-center text-[#3A352F] shadow-[0_10px_34px_-16px_rgba(0,0,0,0.3)]">
            <FileText className="mx-auto size-9 text-primary" strokeWidth={1.4} />
            <h2 className="mt-4 font-serif text-lg leading-snug font-semibold text-[#14110F]">{title}</h2>
            <div className="mt-2 text-[10px] font-bold tracking-[0.16em] text-primary uppercase">Full Manuscript</div>
            <p className="mt-5 text-[11px] leading-[1.6] text-[#6b6256]">Open in Dibbly Create to read every chapter, leave comments, and track changes with your team.</p>
          </div>
        </div>

        {/* details */}
        <div className="flex min-w-0 flex-1 flex-col gap-5 px-[30px] py-7 max-[1199px]:px-4 max-[1199px]:py-5">
          <div className="flex items-start gap-3.5">
            <div className="flex size-[46px] shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#FF5079] via-primary to-[#C7123A] shadow-[0_8px_20px_-8px_var(--red-glow)]">
              <Pencil className="size-6 text-white" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-[19px] leading-[1.15] font-bold tracking-[-0.3px]">{title} — Full Draft</h2>
              <div className="mt-1 flex items-center gap-[7px] text-[12.5px] text-ink-3">
                <span className="rounded-full bg-[var(--red-soft)] px-2 py-0.5 text-[10px] font-bold tracking-[0.04em] text-primary">Dibbly Create</span>
                Opens in your connected document editor
              </div>
            </div>
          </div>

          <div className="flex items-start gap-[11px] rounded-xl border border-border bg-accent px-4 py-[14px]">
            <span className="grid size-8 shrink-0 place-items-center rounded-[9px] border border-border bg-card">
              <MessageSquare className="size-[17px] text-chart-2" strokeWidth={1.9} />
            </span>
            <div>
              <h4 className="text-[13px] font-bold">Comments &amp; tracked changes live in Dibbly Create</h4>
              <p className="mt-0.5 text-[12px] leading-[1.5] text-muted-foreground">
                Leave inline notes, accept or reject edits, and chat with your writer right inside the manuscript.
              </p>
            </div>
          </div>

          {/* Below 1200 the hand-off into Create takes its own full line and
              the remaining actions share the next one. */}
          <div className="flex flex-wrap items-center gap-[11px]">
            {documentLink ? (
              <Button asChild className="gap-[9px] font-bold max-[1199px]:basis-full">
                <a href={documentLink} target="_blank" rel="noreferrer">
                  <Pencil className="size-[18px]" strokeWidth={2} />
                  Open in Dibbly Create
                  <ExternalLink className="size-[18px]" strokeWidth={2} />
                </a>
              </Button>
            ) : (
              <Button disabled className="gap-[9px] font-bold max-[1199px]:basis-full">
                <Pencil className="size-[18px]" strokeWidth={2} />
                Not yet in Create
              </Button>
            )}
            {docFile && (
              <Button asChild variant="outline" className="gap-[7px] max-[1199px]:flex-1">
                <a href={docFile.url} download={docFile.fileName}>
                  <Download className="size-4" strokeWidth={2} />
                  Download {docFile.fileName?.split(".").pop()?.toUpperCase() || "file"}
                </a>
              </Button>
            )}
            {report && (
              <Button asChild variant="outline" className="gap-[7px] max-[1199px]:flex-1">
                <a href={report.url} download={report.fileName} title={report.fileName}>
                  <Download className="size-4" strokeWidth={2} />
                  Download originality report
                </a>
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2.5">
            <AvatarStack people={collaborators} size={28} />
            <div className="text-[12px] text-ink-3">
              <b className="font-semibold text-foreground">You &amp; your team</b> have access in Create
            </div>
          </div>
        </div>
      </div>

      {/* appbar */}
      {(canApprove || canRevise) && (
        <div className="flex flex-wrap items-center gap-4 border-t border-border bg-gradient-to-b from-card to-accent px-[22px] py-4 max-[720px]:gap-3 max-[720px]:px-4 max-[720px]:py-[14px]">
          <div className="max-[720px]:basis-full">
            <h4 className="font-display text-sm font-bold">Ready to approve this draft?</h4>
            <p className="mt-0.5 text-[12px] text-ink-3">
              {sample
                ? "Review in Dibbly Create first. Originality review begins once you approve."
                : "Review in Dibbly Create first. Editing begins once you approve."}
            </p>
          </div>
          <div className="ml-auto flex gap-[9px] max-[720px]:w-full">
            {canRevise && (
              <Button variant="outline" className="gap-[7px] max-[720px]:flex-1" onClick={() => onOpenModal?.("request-revision")}>
                <RotateCcw className="size-4" strokeWidth={2} />
                Request changes
              </Button>
            )}
            {canApprove && (
              <Button className="gap-[7px] font-bold max-[720px]:flex-1" onClick={() => onOpenModal?.("approve-draft")}>
                <Check className="size-4" strokeWidth={2.2} />
                Approve draft
              </Button>
            )}
          </div>
        </div>
      )}
    </PanelCard>
  )
}
